import React from "react";
import { IconCorporateFareBlue, IconXpFlare } from "assets/svg";
import {
  getBadgePublicAssetUrl,
  getPublicAssetUrl,
} from "components/productSellerCompanyCard/productSellerCompanyCard.utils";
import { BADGE_TIER_LABELS } from "common/constants";
import type {
  AdminCompanyQuestProgressDetail,
  AdminCompanyQuestProgressDetailBadge,
  AdminCompanyQuestProgressDetailQuest,
  CompanyQuestStatus,
} from "common/types/questManagement.type";

interface QuestProgressDetailModalProps {
  detail: AdminCompanyQuestProgressDetail | null;
  errorMessage: string;
  isLoading: boolean;
  isOpen: boolean;
  onClose: () => void;
  onRetry: () => void;
}

const BADGE_TIER_ORDER: AdminCompanyQuestProgressDetailBadge["tier"][] = [
  "bronze",
  "silver",
  "gold",
];

const BADGE_TIER_TEXT_COLORS: Record<
  AdminCompanyQuestProgressDetailBadge["tier"],
  string
> = {
  bronze: "text-[#9b4a1c]",
  silver: "text-[#7e8791]",
  gold: "text-[#9a7b00]",
};

const STATUS_LABELS: Record<CompanyQuestStatus, string> = {
  in_progress: "進行中",
  completed: "完了",
  claimed: "完了",
};

const clampProgress = (value: number) => Math.max(0, Math.min(100, value));

const formatNumber = (value: number) => value.toLocaleString("ja-JP");

const getQuestStatusClassName = (status: CompanyQuestStatus) => {
  if (status === "in_progress") {
    return "border border-[#1b4b86] bg-white text-[#1b4b86]";
  }

  return "bg-[linear-gradient(135deg,#1b4b86_0%,#05315e_100%)] text-white";
};

const QuestProgressRow = ({
  quest,
}: {
  quest: AdminCompanyQuestProgressDetailQuest;
}) => {
  const progress = clampProgress(quest.progress_percentage);

  return (
    <div className="w-full">
      <div className="sm:hidden">
        <p className="truncate text-xs font-bold leading-[1.6] text-[#1b4b86]">
          {quest.title}
        </p>
        <div className="mt-1 flex h-[26px] items-center justify-between">
          <span
            className={`flex h-5 w-24 items-center justify-center text-center font-['Noto_Sans_JP'] text-[10px] font-bold leading-[1.6] ${getQuestStatusClassName(
              quest.status,
            )}`}
          >
            {STATUS_LABELS[quest.status]}
          </span>
          <div className="flex items-end">
            <span className="font-['Audiowide'] text-2xl leading-[1.1] text-[#1b4b86]">
              {progress}
            </span>
            <span className="pb-px pl-px text-[11px] font-bold leading-[1.8] text-[#1b4b86]">
              %
            </span>
          </div>
        </div>
        <div className="mt-1 h-3 overflow-hidden rounded-md bg-white">
          <div
            className="h-full rounded-md bg-[linear-gradient(90deg,#d9ff43_0%,#30d9e7_100%)]"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="hidden h-[47px] sm:block">
        <div className="grid h-[26px] grid-cols-[minmax(0,1fr)_96px_71px] items-center gap-x-[10px]">
          <p className="truncate pr-[10px] text-[14px] font-bold leading-[1.6] text-[#1b4b86]">
            {quest.title}
          </p>
          <span
            className={`flex h-[20px] w-[96px] items-center justify-center text-center font-['Noto_Sans_JP'] text-[10px] font-bold leading-[1.6] ${getQuestStatusClassName(
              quest.status,
            )}`}
          >
            {STATUS_LABELS[quest.status]}
          </span>
          <div className="flex h-[29px] items-end justify-end">
            <span className="font-['Audiowide'] text-[26px] leading-[1.1] text-[#1b4b86]">
              {progress}
            </span>
            <span className="pb-px pl-px text-xs font-bold leading-[1.8] text-[#1b4b86]">
              %
            </span>
          </div>
        </div>
        <div className="mt-[5px] h-4 overflow-hidden rounded-lg bg-white">
          <div
            className="h-full rounded-lg bg-[linear-gradient(90deg,#d9ff43_0%,#30d9e7_100%)]"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
};

const BadgeGroup = ({
  badges,
  tier,
}: {
  badges: AdminCompanyQuestProgressDetailBadge[];
  tier: AdminCompanyQuestProgressDetailBadge["tier"];
}) => {
  const filteredBadges = badges.filter((badge) => badge.tier === tier);

  return (
    <div>
      <p className="mb-2.5 text-[15px] font-bold leading-[1.6] text-[#1b4b86] sm:mb-[15px] sm:text-base">
        {BADGE_TIER_LABELS[tier]}バッジ
      </p>
      {filteredBadges.length > 0 ? (
        <div className="grid grid-cols-2 gap-x-[15px] gap-y-[17px] sm:flex sm:flex-wrap sm:gap-x-5">
          {filteredBadges.map((badge) => (
            <div
              className="flex min-h-[134px] min-w-0 flex-col items-center sm:w-[111.67px]"
              key={badge.id}
            >
              <img
                alt={badge.name}
                className="h-[110px] w-[110px] object-contain"
                src={getBadgePublicAssetUrl(
                  badge.code,
                  badge.is_unlocked ? "unlocked" : "locked",
                )}
              />
              <p
                className={`mt-[7px] w-[96px] whitespace-pre-line break-words text-center text-[12px] font-bold leading-[1.4] ${
                  badge.is_unlocked
                    ? BADGE_TIER_TEXT_COLORS[tier]
                    : "text-[#adbbcc]"
                }`}
              >
                {badge.name}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex min-h-[134px] items-center justify-center text-[13px] font-bold text-[#9aaabd]">
          表示できるバッジがありません
        </div>
      )}
    </div>
  );
};

const QuestProgressDetailContent = ({
  detail,
}: {
  detail: AdminCompanyQuestProgressDetail;
}) => {
  const totalXp = detail.xp.total_xp ?? 0;
  const currentLevel = detail.xp.current_level;
  const monthlyXp = detail.xp.claimed_quest_xp_in_current_month;

  return (
    <>
      <div className="flex items-center gap-[15px] sm:gap-6">
        <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-[#eef5fa] text-xl font-bold text-white sm:h-[120px] sm:w-[120px] sm:text-[26px]">
          {detail.company.logo_url ? (
            <img
              alt={detail.company.name || detail.company.code || ""}
              className="h-full w-full rounded-full object-cover"
              src={getPublicAssetUrl(detail.company.logo_url)}
            />
          ) : (
            <IconCorporateFareBlue
              aria-hidden="true"
              className="h-10 w-10 text-[#1b4b86] sm:h-[52px] sm:w-[52px]"
            />
          )}
        </div>
        <div>
          <div className="flex items-center gap-2.5 sm:gap-[14px]">
            <p className="text-2xl font-bold leading-[1.4] text-black sm:text-[32px]">
              {detail.company.name || detail.company.code || "-"}
            </p>
            <span className="bg-[linear-gradient(90deg,#d9ff43_0%,#30d9e7_100%)] px-[8px] py-[3px] text-[14px] font-bold leading-none text-white">
              Lv.{currentLevel ?? "-"}
            </span>
          </div>
          <p className="mt-1 text-xs leading-[1.6] text-black sm:text-lg">
            アクティビティログ
          </p>
        </div>
      </div>

      <section className="mt-5 min-h-[120px] rounded-md border border-[#002959] bg-[linear-gradient(135deg,#1b4b86_0%,#05315e_100%)] px-5 pb-5 pt-[15px] text-white shadow-[0_3px_8px_rgba(0,0,0,0.3)] sm:mt-10 sm:min-h-[158px] sm:px-[30px] sm:py-5">
        <div className="border-b border-white/50 pb-[7px] sm:pb-2.5">
          <p className="text-lg font-bold leading-[1.6] sm:text-xl">
            現在の累計XP
          </p>
        </div>
        <div className="mt-[15px] flex items-end justify-between sm:hidden">
          <p className="font-['Audiowide'] text-[30px] leading-none text-[#d9ff43]">
            {formatNumber(totalXp)}
            <span className="ml-1.5 text-base">XP</span>
          </p>
          {monthlyXp && monthlyXp > 0 ? (
            <p className="inline-flex items-center gap-1 whitespace-nowrap bg-[linear-gradient(90deg,#d9ff43_0%,#30d9e7_100%)] px-1.5 py-0.5 font-['Audiowide'] text-[11px] leading-[1.2] text-[#1b4b86]">
              + {formatNumber(monthlyXp)} XP
              <IconXpFlare aria-hidden="true" className="h-2.5 w-2.5" />
            </p>
          ) : null}
        </div>
        <div className="quest-level-frame mt-5 hidden sm:block">
          <div className="quest-level-frame__inner">
            <p className="absolute left-0 top-0 flex h-[46px] w-[210px] items-end font-['Audiowide'] text-[46px] leading-none text-[#d9ff43]">
              {formatNumber(totalXp)}
              <span className="ml-[8px] text-[24px] text-[#d9ff43]">XP</span>
            </p>
            {monthlyXp && monthlyXp > 0 ? (
              <div className="absolute left-[225px] top-[13px] h-[33px] w-[105px] pt-[5px]">
                <p className="inline-flex h-[23px] min-w-[105px] items-center justify-between gap-[5px] whitespace-nowrap bg-[linear-gradient(90deg,#d9ff43_0%,#30d9e7_100%)] px-[7px] py-[2px] font-['Audiowide'] text-[16px] leading-[1.2] text-[#1b4b86]">
                  <span className="whitespace-nowrap">
                    + {formatNumber(monthlyXp)} XP
                  </span>
                  <IconXpFlare
                    aria-hidden="true"
                    className="h-[14px] w-[14px]"
                  />
                </p>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      <div className="mt-[20px] space-y-[15px]">
        <section className="border border-[#1b4b86] p-[5px]">
          <div className="rounded-[6px] bg-[#eef5fa] px-[20px] py-[20px]">
            <div className="border-b border-[#d5d5d5] pb-[10px]">
              <h3 className="text-lg font-bold leading-[1.6] text-[#1b4b86] sm:text-xl">
                進行中のクエスト進捗
              </h3>
            </div>

            <div className="mt-5 space-y-[15px] sm:space-y-5">
              {detail.quests.length > 0 ? (
                detail.quests.map((quest) => (
                  <QuestProgressRow key={quest.quest_id} quest={quest} />
                ))
              ) : (
                <div className="flex min-h-[120px] items-center justify-center text-[14px] font-bold text-[#1b4b86]">
                  表示できるクエスト進捗がありません
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="border border-[#1b4b86] p-[5px]">
          <div className="rounded-[6px] bg-[#eef5fa] px-[20px] py-[20px]">
            <div className="border-b border-[#d5d5d5] pb-[10px]">
              <h3 className="text-lg font-bold leading-[1.6] text-[#1b4b86] sm:text-xl">
                獲得バッジ
              </h3>
            </div>
            <div className="mt-5 space-y-5 sm:space-y-[30px]">
              {BADGE_TIER_ORDER.map((tier) => (
                <BadgeGroup badges={detail.badges} key={tier} tier={tier} />
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

const QuestProgressDetailModal = ({
  detail,
  errorMessage,
  isLoading,
  isOpen,
  onClose,
  onRetry,
}: QuestProgressDetailModalProps) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-black/80 px-[15px] py-6 sm:px-6 sm:py-10"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="mx-auto w-full max-w-[900px] rounded-[30px] bg-white px-[30px] py-[50px] shadow-[0_6px_18px_rgba(0,0,0,0.25)] sm:px-10 sm:py-10"
        onClick={(event) => event.stopPropagation()}
        role="presentation"
      >
        {isLoading ? (
          <div className="flex min-h-[520px] items-center justify-center text-[16px] font-bold text-[#1b4b86]">
            進捗詳細データを読み込み中です。
          </div>
        ) : errorMessage ? (
          <div className="flex min-h-[520px] flex-col items-center justify-center gap-[18px] text-center">
            <p className="text-[16px] font-bold text-[#1b4b86]">
              {errorMessage}
            </p>
            <div className="flex gap-[12px]">
              <button
                className="h-[42px] w-[150px] rounded-[6px] bg-[#eef5fa] text-[14px] font-bold text-[#1b4b86]"
                onClick={onClose}
                type="button"
              >
                閉じる
              </button>
              <button
                className="h-[42px] w-[150px] rounded-[6px] bg-[#1b4b86] text-[14px] font-bold text-white"
                onClick={onRetry}
                type="button"
              >
                再読み込み
              </button>
            </div>
          </div>
        ) : detail ? (
          <>
            <QuestProgressDetailContent detail={detail} />
            <div className="mt-[40px] flex justify-center">
              <button
                className="h-[50px] w-full max-w-[340px] rounded-md bg-[#1b4b86] text-base font-bold text-white sm:h-[60px] sm:text-lg"
                onClick={onClose}
                type="button"
              >
                閉じる
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default QuestProgressDetailModal;
